import { m } from '@pricedrop/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { addMonths, format } from 'date-fns';
import { he } from 'date-fns/locale/he';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import type { Notification, NotificationType } from '@/lib/notifications';
import {
  EXPIRATION_OPTIONS,
  NOTIFICATION_TYPE_OPTIONS,
  requiresPriceThreshold,
  useCreateNotification,
  useDeleteNotification,
  useUpdateNotification,
} from '@/lib/notifications';
import { createCookieNotificationsClient } from '@/lib/notifications/data-client';
import { NotificationForm } from './notification-form';
import { ResponsiveDialog } from './responsive-dialog';

interface ItemNotificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemUin: number;
  itemName: string;
  currentPrice: number | null;
  existingNotification?: Notification | null;
  onSuccess?: () => void;
  onDelete?: () => void;
  scope?: 'ITEM' | 'TAGS';
  tags?: number[];
  tagNames?: string[];
  brandIds?: number[] | null;
  brandNames?: string[] | null;
}

interface TagsFormValues {
  type: NotificationType;
  priceThreshold: number;
  expiresAt: Date | null;
}

function computeTagsDefaults(existingNotification?: Notification | null) {
  const defaultType: NotificationType = existingNotification?.type ?? 'PRICE_DROP';
  const defaultThreshold: number = existingNotification?.priceThreshold ?? 100;
  const defaultExpiresAt: Date = existingNotification?.expiresAt
    ? new Date(existingNotification.expiresAt as string)
    : addMonths(new Date(), 3);
  return { defaultType, defaultThreshold, defaultExpiresAt };
}

/**
 * TAGS-scope dialog body. Extracted as a child component so the parent can
 * switch between the TAGS path (inline) and the ITEM path (delegated to
 * `<NotificationForm>`) without running both sets of hooks.
 *
 * This path remains a verbatim port of the pre-extraction TAGS code path.
 * M3 will fold it into `NotificationForm` with a scope discriminator.
 */
function TagsDialogForm({
  onOpenChange,
  existingNotification,
  onSuccess,
  onDelete,
  tags,
  tagNames,
  brandIds,
  brandNames,
  openKey,
}: {
  onOpenChange: (open: boolean) => void;
  existingNotification: Notification | null | undefined;
  onSuccess?: () => void;
  onDelete?: () => void;
  tags?: number[];
  tagNames?: string[];
  brandIds?: number[] | null;
  brandNames?: string[] | null;
  /** Incremented each time the dialog opens so the form resets. */
  openKey: number;
}) {
  const isEditing = !!existingNotification;
  const { defaultType, defaultThreshold, defaultExpiresAt } = React.useMemo(
    () => computeTagsDefaults(existingNotification),
    [existingNotification],
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<TagsFormValues>({
    defaultValues: {
      type: defaultType,
      priceThreshold: defaultThreshold,
      expiresAt: defaultExpiresAt,
    },
  });

  const createMutation = useCreateNotification();
  const updateMutation = useUpdateNotification();
  const deleteMutation = useDeleteNotification();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

  const isLoading =
    isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
  const watchedType = watch('type');
  // For TAGS scope, the slider gating is `requiresPriceThreshold(type) && true` (always on).
  const showSlider = requiresPriceThreshold(watchedType);

  const availableTypeOptions = React.useMemo(
    () =>
      NOTIFICATION_TYPE_OPTIONS.filter((option) => {
        if (option.value === 'BACK_IN_STOCK') return false;
        if (
          option.value === 'PRICE_BELOW_THRESHOLD' ||
          option.value === 'LOWEST_PRICE' ||
          option.value === 'PRICE_DROP'
        )
          return true;
        return true; // ANY_CHANGE always available
      }),
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-run on openKey change
  React.useEffect(() => {
    reset({
      type: defaultType,
      priceThreshold: defaultThreshold,
      expiresAt: defaultExpiresAt,
    });
    setShowDeleteConfirmation(false);
  }, [openKey, defaultType, defaultThreshold, defaultExpiresAt, reset]);

  async function onSubmit(values: TagsFormValues) {
    try {
      if (isEditing && existingNotification) {
        await updateMutation.mutateAsync({
          id: existingNotification.id,
          type: values.type,
          priceThreshold: requiresPriceThreshold(values.type) ? values.priceThreshold : undefined,
          expiresAt: values.expiresAt?.toISOString(),
        });
        toast.success(m.notifications_toast_updated());
      } else {
        await createMutation.mutateAsync({
          scope: 'TAGS',
          type: values.type,
          tags: tags ?? [],
          brandIds: brandIds && brandIds.length > 0 ? brandIds : undefined,
          priceThreshold: requiresPriceThreshold(values.type) ? values.priceThreshold : undefined,
          expiresAt: values.expiresAt?.toISOString(),
        });
        toast.success(m.notifications_toast_created());
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CONFLICT') {
        toast.info(m.notifications_toast_duplicate());
        return;
      }
      toast.error(m.notifications_toast_error());
    }
  }

  async function handleDelete() {
    if (!existingNotification) return;
    try {
      await deleteMutation.mutateAsync({
        id: existingNotification.id,
        itemUin: existingNotification.itemUin,
        tags: existingNotification.tags,
        brandIds: existingNotification.brandIds ?? undefined,
      });
      toast.success(m.notifications_toast_deleted());
      onOpenChange(false);
      onDelete?.();
    } catch {
      toast.error(m.notifications_toast_error());
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="rounded-lg border border-input bg-muted/30 p-3 space-y-2">
        <p className="text-sm font-medium text-muted-foreground">
          {m.notifications_tags_reference_label()}
        </p>
        <div className="flex flex-wrap gap-2">
          {(tagNames ?? []).map((name) => (
            <span
              key={name}
              className="inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {name}
            </span>
          ))}
        </div>
        {brandNames && brandNames.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {brandNames.map((name) => (
              <span
                key={name}
                role="img"
                aria-label={m.notifications_brand_pill_label()}
                className="inline-flex items-center rounded-full border border-purple-200 bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800 dark:border-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
              >
                {name}
              </span>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {brandNames && brandNames.length > 0
            ? m.notifications_tags_brand_reference_hint()
            : m.notifications_tags_reference_hint()}
        </p>
      </div>

      <div className="space-y-2">
        <Label>{m.notifications_type_label()}</Label>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {showSlider && (
        <div className="space-y-3">
          <Label>{m.notifications_tags_threshold_label()}</Label>
          <Controller
            control={control}
            name="priceThreshold"
            render={({ field }) => (
              <div className="space-y-2">
                <Input
                  type="number"
                  min={1}
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  className="w-full text-center text-lg font-bold"
                />
              </div>
            )}
          />
          <p className="text-xs text-muted-foreground">{m.notifications_tags_threshold_hint()}</p>
        </div>
      )}

      <div className="space-y-3">
        <Label>{m.notifications_expiration_label()}</Label>
        <div className="flex w-full items-center gap-2 rounded-lg border border-input bg-background p-3 text-sm">
          <span>
            {watch('expiresAt') ? format(watch('expiresAt') as Date, 'PPP', { locale: he }) : ''}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {EXPIRATION_OPTIONS.map((option) => {
            const targetDate = addMonths(new Date(), option.months);
            const currentExpiresAt = watch('expiresAt');
            const isActive =
              currentExpiresAt &&
              Math.abs(currentExpiresAt.getTime() - targetDate.getTime()) < 24 * 60 * 60 * 1000;
            return (
              <Button
                key={option.months}
                type="button"
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                className="h-10"
                onClick={() => setValue('expiresAt', targetDate)}
              >
                {option.label()}
              </Button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">{m.notifications_expiration_hint()}</p>
      </div>

      {showDeleteConfirmation ? (
        <div className="space-y-4 rounded-lg border-2 border-destructive/20 bg-destructive/10 p-4">
          <div className="text-center">
            <p className="mb-2 font-semibold text-destructive">
              {m.notifications_delete_confirm_title()}
            </p>
            <p className="text-sm text-muted-foreground">
              {m.notifications_delete_confirm_description()}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirmation(false)}
              disabled={isLoading}
              className="flex-1"
            >
              {m.notifications_delete_confirm_cancel()}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isLoading}
              className="flex-1"
            >
              {deleteMutation.isPending
                ? m.notifications_delete_deleting()
                : m.notifications_delete_confirm_delete()}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <Button type="submit" disabled={isLoading} className="w-full rounded-lg py-3 font-medium">
            {isSubmitting
              ? m.notifications_submit_saving()
              : isEditing
                ? m.notifications_submit_update()
                : m.notifications_submit_create()}
          </Button>
          {isEditing && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirmation(true)}
              disabled={isLoading}
              className="w-full rounded-lg border-destructive py-3 font-medium text-destructive hover:bg-destructive hover:text-destructive-foreground"
            >
              {m.notifications_delete_button()}
            </Button>
          )}
        </div>
      )}
    </form>
  );
}

export function ItemNotificationDialog({
  open,
  onOpenChange,
  itemUin,
  itemName,
  currentPrice,
  existingNotification,
  onSuccess,
  onDelete,
  scope = 'ITEM',
  tags,
  tagNames,
  brandIds,
  brandNames,
}: ItemNotificationDialogProps) {
  const queryClient = useQueryClient();
  // Cookie-auth data client with cache invalidation wired in — so that
  // `useItemNotificationMap()` / `useItemNotification()` consumers elsewhere
  // in the tree refresh after mutations from this dialog.
  const dataClient = React.useMemo(
    () => createCookieNotificationsClient(queryClient),
    [queryClient],
  );
  // `openKey` bumps every time the dialog transitions closed → open. Children
  // key/effect off this to re-seed form defaults. This mirrors the pre-refactor
  // `useEffect(..., [open, ...])` reset behavior in the monolithic dialog.
  const [openKey, setOpenKey] = React.useState(0);
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (!prevOpenRef.current && open) {
      setOpenKey((k) => k + 1);
    }
    prevOpenRef.current = open;
  }, [open]);

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={m.notifications_dialog_title()}
      description={
        scope === 'TAGS'
          ? brandNames && brandNames.length > 0
            ? m.notifications_tags_brand_dialog_description()
            : m.notifications_tags_dialog_description()
          : m.notifications_dialog_description()
      }
    >
      {scope === 'TAGS' ? (
        <TagsDialogForm
          openKey={openKey}
          onOpenChange={onOpenChange}
          existingNotification={existingNotification}
          onSuccess={onSuccess}
          onDelete={onDelete}
          tags={tags}
          tagNames={tagNames}
          brandIds={brandIds}
          brandNames={brandNames}
        />
      ) : (
        <NotificationForm
          key={openKey}
          itemUin={itemUin}
          itemName={itemName}
          currentPrice={currentPrice}
          existingNotification={existingNotification ?? null}
          dataClient={dataClient}
          onSuccess={() => {
            onOpenChange(false);
            onSuccess?.();
          }}
          onDelete={() => {
            onOpenChange(false);
            onDelete?.();
          }}
          variant="dialog"
        />
      )}
    </ResponsiveDialog>
  );
}
